import type { Direction } from "../../engine/direction";
import { ParticleType } from "../../engine/particleType";
import type { Particle } from "./particle";
import { BloodParticle } from "./types/bloodParticle";
import { BlueBloodParticle } from "./types/blueBloodParticle";
import { ConcreteDebrisParticle } from "./types/concreteDebrisParticle";
import { LeafDebrisParticle } from "./types/leafDebrisParticle";
import { MetalDebrisParticle } from "./types/metalDebrisParticle";
import { BulletTrailParticle } from "./types/bulletTrailParticle";
import { MissileExhaustParticle } from "./types/missileExhaustParticle";
import { LargeExplosionParticle } from "./types/largeExplosionParticle";
import { LargeGroundExplosionParticle } from "./types/largeGroundExplosionParticle";
import { PlasticDebrisParticle } from "./types/plasticDebrisParticle";
import { LargeRobotDebrisParticle } from "./types/largeRobotDebrisParticle";
import { RobotDebrisParticle } from "./types/robotDebrisParticle";
import { RockDebrisParticle } from "./types/rockDebrisParticle";
import { GreenDebrisParticle } from "./types/greenDebrisParticle";
import { MuzzleFlashParticle } from "./types/muzzleFlashParticle";
import { IceBurstParticle } from "./types/iceBurstParticle";
import { SolidifyBurstParticle } from "./types/solidifyBurstParticle";
import { SolidifyMobBurstParticle } from "./types/solidifyMobBurstParticle";
import { ToxicGasBurstParticle } from "./types/toxicGasBurstParticle";
import { PlasmaCloudParticle } from "./types/plasmaCloudParticle";
import { PlasmaSparkParticle } from "./types/plasmaSparkParticle";
import { GasLeakParticle } from "./types/gasLeakParticle";
import { WoodDebrisParticle } from "./types/woodDebrisParticle";
import { BloodDripParticle } from "./types/bloodDripParticle";
import { DamageSmokeParticle } from "./types/damageSmokeParticle";
import { BileSmokeParticle } from "./types/bileSmokeParticle";
import { StepDustParticle } from "./types/stepDustParticle";
import { AirBlastParticle } from "./types/airBlastParticle";
import { AirBlastBurstParticle } from "./types/airBlastBurstParticle";
import { IceDripParticle } from "./types/iceDripParticle";
import { JetpackFlameParticle } from "./types/jetpackFlameParticle";
import { GoldSparkleParticle } from "./types/goldSparkleParticle";
import { BIG_SPRITE_RADIUS, BlackHoleSuctionParticle } from "./types/blackHoleSuctionParticle";
import { TornGroundDustParticle } from "./types/tornGroundDustParticle";

export interface ParticleTypeConfig {
	create: (originX: number, originY: number, direction?: Direction) => Particle;
	emitIntervalMs?: number;
	spawnOffset?: (direction?: Direction) => { x: number; y: number };
}

const particleTypeConfigs: Record<ParticleType, ParticleTypeConfig> = {
	[ParticleType.ROCK_DEBRIS]: {
		create: (ox, oy, dir) => new RockDebrisParticle(ox, oy, dir),
	},
	[ParticleType.GREEN_DEBRIS]: {
		create: (ox, oy, dir) => new GreenDebrisParticle(ox, oy, dir),
	},
	[ParticleType.CONCRETE_DEBRIS]: {
		create: (ox, oy, dir) => new ConcreteDebrisParticle(ox, oy, dir),
	},
	[ParticleType.WOOD_DEBRIS]: {
		create: (ox, oy, dir) => new WoodDebrisParticle(ox, oy, dir),
	},
	[ParticleType.METAL_DEBRIS]: {
		create: (ox, oy, dir) => new MetalDebrisParticle(ox, oy, dir),
	},
	[ParticleType.PLASTIC_DEBRIS]: {
		create: (ox, oy, dir) => new PlasticDebrisParticle(ox, oy, dir),
	},
	[ParticleType.LEAF_DEBRIS]: {
		create: (ox, oy, dir) => new LeafDebrisParticle(ox, oy, dir),
	},
	[ParticleType.BLOOD]: {
		create: (ox, oy, dir) => new BloodParticle(ox, oy, dir),
	},
	[ParticleType.BLUE_BLOOD]: {
		create: (ox, oy, dir) => new BlueBloodParticle(ox, oy, dir),
	},
	[ParticleType.ROBOT_DEBRIS]: {
		create: (ox, oy, dir) => new RobotDebrisParticle(ox, oy, dir),
	},
	[ParticleType.LARGE_ROBOT_DEBRIS]: {
		create: (ox, oy, dir) => new LargeRobotDebrisParticle(ox, oy, dir),
	},
	[ParticleType.BUBBLE_TRAIL]: {
		create: (ox, oy, dir) => new BulletTrailParticle(ox, oy, dir, { r: 0x30, g: 0xb0, b: 0x12 }),
		emitIntervalMs: BulletTrailParticle.emitIntervalMs,
		spawnOffset: BulletTrailParticle.spawnOffset,
	},
	[ParticleType.ELECTRO_TRAIL]: {
		create: (ox, oy, dir) => new BulletTrailParticle(ox, oy, dir, { r: 0xab, g: 0xda, b: 0xff }),
		emitIntervalMs: BulletTrailParticle.emitIntervalMs,
		spawnOffset: BulletTrailParticle.spawnOffset,
	},
	[ParticleType.DRILL_TRAIL]: {
		create: (ox, oy, dir) => new BulletTrailParticle(ox, oy, dir, { r: 0xdb, g: 0xdb, b: 0xdb }),
		emitIntervalMs: BulletTrailParticle.emitIntervalMs,
		spawnOffset: BulletTrailParticle.spawnOffset,
	},
	[ParticleType.BULLET_TRAIL]: {
		create: (ox, oy, dir) => new BulletTrailParticle(ox, oy, dir),
		emitIntervalMs: BulletTrailParticle.emitIntervalMs,
		spawnOffset: BulletTrailParticle.spawnOffset,
	},
	[ParticleType.MISSILE_EXHAUST]: {
		create: (ox, oy, dir) => new MissileExhaustParticle(ox, oy, dir),
		emitIntervalMs: MissileExhaustParticle.emitIntervalMs,
		spawnOffset: MissileExhaustParticle.spawnOffset,
	},
	[ParticleType.LARGE_EXPLOSION]: {
		create: (ox, oy) => new LargeExplosionParticle(ox, oy),
	},
	[ParticleType.LARGE_GROUND_EXPLOSION]: {
		create: (ox, oy) => new LargeGroundExplosionParticle(ox, oy),
	},
	[ParticleType.MUZZLE_FLASH]: {
		create: (ox, oy, dir) => new MuzzleFlashParticle(ox, oy, dir),
	},
	[ParticleType.MUZZLE_FLASH_GREEN]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				color: { r: 0xa8, g: 0xff, b: 0x94 },
			}),
	},
	[ParticleType.MUZZLE_FLASH_GREEN_WIDE]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				color: { r: 0xa8, g: 0xff, b: 0x94 },
				coneHalfAngle: Math.PI / 3,
			}),
	},
	[ParticleType.MUZZLE_FLASH_BLUE]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				color: { r: 0x00, g: 0x8f, b: 0xff },
			}),
	},
	[ParticleType.MUZZLE_FLASH_PURPLE_WIDE]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				color: { r: 0xcf, g: 0x75, b: 0xf9 },
				coneHalfAngle: Math.PI / 2.5,
			}),
	},
	[ParticleType.MUZZLE_FLASH_FIRE]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				speed: [12.0, 16.0],
			}),
	},
	[ParticleType.MUZZLE_FLASH_WHITE]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				color: { r: 0xff, g: 0xff, b: 0xff },
				coneHalfAngle: Math.PI / 4.5,
			}),
	},
	[ParticleType.MUZZLE_FLASH_ICE]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				color: { r: 0xff, g: 0xff, b: 0xff },
			}),
	},
	[ParticleType.MUZZLE_FLASH_BILE]: {
		create: (ox, oy, dir) =>
			new MuzzleFlashParticle(ox, oy, dir, {
				color: { r: 0xeb, g: 0xc2, b: 0x70 },
				size: [0.7, 0.45],
				ttl: [150, 280],
				opacityHold: 0.95,
			}),
	},
	[ParticleType.BLOOD_DRIP]: {
		create: (ox, oy) => new BloodDripParticle(ox, oy),
		emitIntervalMs: BloodDripParticle.emitIntervalMs,
	},
	[ParticleType.BLUE_BLOOD_DRIP]: {
		create: (ox, oy) => new BloodDripParticle(ox, oy, { r: 25, g: 50, b: 150 }),
		emitIntervalMs: BloodDripParticle.emitIntervalMs,
	},
	[ParticleType.DAMAGE_SMOKE]: {
		create: (ox, oy) => new DamageSmokeParticle(ox, oy),
		emitIntervalMs: DamageSmokeParticle.emitIntervalMs,
	},
	[ParticleType.BILE_SMOKE]: {
		create: (ox, oy) => new BileSmokeParticle(ox, oy),
		emitIntervalMs: BileSmokeParticle.emitIntervalMs,
	},
	[ParticleType.BILE_SMOKE_SMALL]: {
		create: (ox, oy) => new BileSmokeParticle(ox, oy, 0.5),
		emitIntervalMs: BileSmokeParticle.emitIntervalSmallMs,
	},
	[ParticleType.STEP_DUST]: {
		create: (ox, oy) => new StepDustParticle(ox, oy),
	},
	[ParticleType.AIR_BLAST_BURST]: {
		create: (ox, oy) => new AirBlastBurstParticle(ox, oy),
	},
	[ParticleType.AIR_BLAST_TRAIL]: {
		create: (ox, oy, dir) => new AirBlastParticle(ox, oy, dir),
		emitIntervalMs: AirBlastParticle.emitIntervalMs,
		spawnOffset: AirBlastParticle.spawnOffset,
	},
	[ParticleType.ICE_DRIP]: {
		create: (ox, oy) => new IceDripParticle(ox, oy),
		emitIntervalMs: IceDripParticle.emitIntervalMs,
	},
	[ParticleType.ICE_BURST]: {
		create: (ox, oy) => new IceBurstParticle(ox, oy),
	},
	[ParticleType.SOLIDIFY_BURST]: {
		create: (ox, oy) => new SolidifyBurstParticle(ox, oy),
	},
	[ParticleType.SOLIDIFY_MOB_BURST]: {
		create: (ox, oy) => new SolidifyMobBurstParticle(ox, oy),
		spawnOffset: () => {
			const angle = Math.random() * Math.PI * 2;
			const r = 0.3;
			return { x: Math.cos(angle) * r, y: Math.sin(angle) * r };
		},
	},
	[ParticleType.TOXIC_GAS_BURST]: {
		create: (ox, oy) => new ToxicGasBurstParticle(ox, oy),
	},
	[ParticleType.PLASMA_CLOUD]: {
		create: (ox, oy) => new PlasmaCloudParticle(ox, oy),
	},
	[ParticleType.PLASMA_SPARK]: {
		create: (ox, oy) => new PlasmaSparkParticle(ox, oy),
	},
	[ParticleType.GAS_LEAK]: {
		create: (ox, oy) => new GasLeakParticle(ox, oy),
		emitIntervalMs: GasLeakParticle.emitIntervalMs,
	},
	[ParticleType.JETPACK_FLAME]: {
		create: (ox, oy) => new JetpackFlameParticle(ox, oy),
		emitIntervalMs: JetpackFlameParticle.emitIntervalMs,
	},
	[ParticleType.GOLD_SPARKLE]: {
		create: (ox, oy) => new GoldSparkleParticle(ox, oy),
		emitIntervalMs: GoldSparkleParticle.emitIntervalMs,
	},
	[ParticleType.BLACK_HOLE_SUCTION]: {
		create: (ox, oy) => new BlackHoleSuctionParticle(ox, oy),
		emitIntervalMs: BlackHoleSuctionParticle.emitIntervalMs,
	},
	[ParticleType.BLACK_HOLE_SUCTION_BIG]: {
		create: (ox, oy) => new BlackHoleSuctionParticle(ox, oy, BIG_SPRITE_RADIUS),
		emitIntervalMs: BlackHoleSuctionParticle.emitIntervalMs,
	},
	[ParticleType.TORN_GROUND_DUST]: {
		create: (ox, oy) => new TornGroundDustParticle(ox, oy),
		emitIntervalMs: TornGroundDustParticle.emitIntervalMs,
	},
};

export function getParticleTypeConfig(type: ParticleType): ParticleTypeConfig {
	const config = particleTypeConfigs[type];
	if (!config) {
		throw new Error(`Unknown particle type: ${type}`);
	}
	return config;
}
